// Affiliate-lenker: gjenkjenning, merking og sporing.
//
// Prinsipp (Stephens regel 13.7): en affiliate-lenke legges KUN inn der
// produktet allerede er omtalt redaksjonelt på faglig grunnlag — anbefalingen
// er «bonus til leseren», aldri grunnen til at produktet står der. Vi endrer
// aldri en anbefaling for å matche en partner.
//
// Teknisk: lenkene i artiklene er vanlige markdown-lenker. Artikkelmalen
// kjenner igjen affiliate-vertene under og rendrer dem med rel="sponsored"
// + klikksporing, så ingen trenger å huske noe spesielt ved skriving.
//
// Partnere per 19.8.2026: TradeDoubler (Philips Home Appliances NO, program
// 402084, site 3489581). Produkt-URL-ene i TradeDoublers feed ER ferdige
// sporingslenker — de skal ikke pakkes inn i deeplink-generatoren.

const AFFILIATE_HOSTS = [
  'clk.tradedoubler.com',
  'pdt.tradedoubler.com',
  'clkuk.tradedoubler.com',
  'go.adt284.net', // Adtraction
];

// Annonsøren MÅ navngis i merkingen — det er ikke vår stilpreferanse, men et
// programvilkår hos Kost1: «innlegget inneholder reklame gjennom annonselenker
// til x», og brudd avslutter samarbeidet. Verten alene holder ikke som nøkkel:
// TradeDoubler-vertene betjener både Philips og Wolt, så vi kjenner igjen på
// program-/annonsør-ID-en inne i URL-en.
const AFFILIATE_PARTNERS: { kjennetegn: string; navn: string }[] = [
  { kjennetegn: 'p(402084)', navn: 'Philips' },
  { kjennetegn: 'p(332863)', navn: 'Wolt' },
  { kjennetegn: 'a=2085431311', navn: 'Kost1' },
];

/** Hvilke annonsører er lenket i denne teksten? Rekkefølgen følger listen over. */
export function affiliatePartnersIn(content: string): string[] {
  return AFFILIATE_PARTNERS.filter(p => content.includes(p.kjennetegn)).map(p => p.navn);
}

/** Hvilket nettverk og hvilken annonsør er dette klikket? Brukes til GA4, så
 *  vi kan skille Kost1 fra Philips fra Wolt i rapportene. Var hardkodet til
 *  «tradedoubler» fram til 31.8.2026 — det var riktig så lenge TradeDoubler var
 *  eneste nettverk, og ble feil i samme øyeblikk Adtraction kom til. */
export function affiliateMetaFor(href: string): { nettverk: string; annonsor: string } {
  const treff = AFFILIATE_PARTNERS.find(x => href.includes(x.kjennetegn));
  const nettverk = href.includes('go.adt284.net') ? 'adtraction'
    : href.includes('tradedoubler.com') ? 'tradedoubler'
    : 'ukjent';
  return { nettverk, annonsor: treff ? treff.navn : 'ukjent' };
}

/** Merketeksten, med annonsørene navngitt. Bygges av innholdet, så den aldri
 *  kan komme i utakt med lenkene som faktisk står i artikkelen. */
export function affiliateDisclosure(content: string): string {
  const navn = affiliatePartnersIn(content);
  const til = navn.length === 0 ? ''
    : navn.length === 1 ? ` til ${navn[0]}`
    : ` til ${navn.slice(0, -1).join(', ')} og ${navn[navn.length - 1]}`;
  return `Innlegget inneholder reklame gjennom annonselenker${til}. Kjøper du via dem, kan vi få provisjon — det påvirker ikke hva vi anbefaler, og koster deg ingenting ekstra.`;
}

/** Er dette en affiliate-lenke som må merkes og spores? */
export function isAffiliateUrl(href: string): boolean {
  if (!href.startsWith('http')) return false;
  return AFFILIATE_HOSTS.some(h => href.includes(h));
}

/** Inneholder artikkelinnholdet minst én affiliate-lenke? Styrer merkingen
 *  øverst i artikkelen — automatisk, så den aldri glemmes. */
export function hasAffiliateLinks(content: string): boolean {
  return AFFILIATE_HOSTS.some(h => content.includes(h));
}

// Reservetekst når ingen kjent annonsør gjenkjennes (skal normalt ikke skje —
// affiliateDisclosure() bygger den navngitte varianten).
export const AFFILIATE_DISCLOSURE = affiliateDisclosure('');

// ---- Philips Home Appliances NO (TradeDoubler 402084) ----
// Sporingsklare produkt-URL-er fra feeden (fid 258717). Oppdater ved behov;
// modell-ID-en i URL-en er nøkkelen som ikke bør endres uten å sjekke feeden.
export const PHILIPS_URLS = {
  // airfryer2000 (NA211_00R1) fjernet 24.8.2026 — utsolgt, produktsiden 404-er.
  airfryer5000xxl:
    'https://pdt.tradedoubler.com/click?a(3489581)p(402084)product(258717-HD9285%2F90R1)ttid(19)url(https%3A%2F%2Fwww.home-appliances.philips%2Fno%2Fno%2Fp%2FHD9285_90R1)',
  // Voucher-lenker (gyldige 29.4.2026–29.3.2027)
  gratisFraktOver250: 'https://clk.tradedoubler.com/click?a(3489581)p(402084)ttid(13)url()',
  nyhetsbrev10: 'https://clk.tradedoubler.com/click?a(3489581)p(402084)ttid(13)url()',
} as const;

// ---- Wolt NOR (TradeDoubler 332863) ----
// Godkjent 24.8.2026.
//
// TO TING SOM STYRER HVOR LENKEN KAN STÅ:
//
// 1. Provisjonen er FTU — «first time user». Sluttlenken bærer kampanjetaggen
//    NOR_FTU_CPA_TradeDoubler_3489581, altså betaling kun for NYE Wolt-brukere.
//    Et klikk fra noen som allerede bruker Wolt gir null. Derfor er lesere som
//    allerede bestiller takeaway ofte feil publikum, selv om temaet passer.
//    (Utledet av kampanjenavnet i redirect-kjeden — bekreft mot programvilkårene
//    i TradeDoubler før noe skaleres på denne antakelsen.)
//
// 2. Tom url() sender brukeren til wolt.com/en/nor — ENGELSK. Destinasjonen må
//    settes eksplisitt til den norske. Begge verifisert mot ekte redirect
//    24.8.2026; TradeDoubler gjør et fingerprint-hopp først og lander deretter
//    på wolt.com med tduid-parameteren.
export function woltDeeplink(dest = 'https://wolt.com/nb/nor'): string {
  return `https://clk.tradedoubler.com/click?a(3489581)p(332863)ttid(13)url(${encodeURIComponent(dest)})`;
}

/** Ferdig sporingslenke til Wolt Norge (norsk språkversjon). */
export const WOLT_NO_URL = woltDeeplink();

// ---- Produktkort ----
// Bilder kommer fra Philips' egen produktfeed (dam.versuni.com) — ekte
// pressebilder levert nettopp for dette formålet, i tråd med regelen om at
// produktbilder aldri skal være AI-genererte.
// Prisen er et øyeblikksbilde og vises ALLTID med dato, så en utdatert pris
// aldri fremstår som dagens. Sjekk mot feeden ved oppdatering.
export type AffiliateProduct = {
  navn: string;
  bilde: string;
  alt: string;
  pris: number;
  prisSjekket: string;
  url: string;
  passerTil: string;
};

export const AFFILIATE_PRODUCTS: Record<string, AffiliateProduct[]> = {
  'airfryer-guide': [
    // 2000-serien (NA211_00R1) er FJERNET 24.8.2026: Philips melder «currently out
    // of stock» og produktsiden 404-er, så affiliatelenka sendte leseren til en
    // død side. Legg den ikke tilbake uten å ha åpnet URL-en og sett en pris.
    {
      // «Renovert» MÅ stå i navnet. Dette er Philips' oppussede utgave — det er
      // derfor den koster 705 kr mot 2 799 kr veiledende. Uten ordet framstår
      // prisen som et røverkjøp på et nytt apparat, og det er villedende
      // markedsføring (mfl. §§ 6–8). Verifisert på Philips' egen produktside 24.8.
      navn: 'Philips 5000-serien XXL, 7,2 l — renovert',
      bilde: 'https://dam.versuni.com/transform/webp515x515/62e76a38-eca3-46ae-91eb-36eb20938391/RFT-HD9285.webp',
      alt: 'Philips 5000-serien XXL airfryer i sort, renovert utgave',
      pris: 705,
      prisSjekket: '24. august 2026',
      url: PHILIPS_URLS.airfryer5000xxl,
      passerTil: 'Familiemiddag, 7,2 liter. Fabrikkrenovert av Philips og selges med deres garanti — derfor prisen mot 2 799 kr veiledende.',
    },
  ],
};


// ---- Kost1 NO (Adtraction, merke 2085431308 / annonse 2085431311) ----
// Godkjent 20.8.2026, vilkår avlest i dashbordet 31.8.2026.
//
// 10 % per salg · 30 dagers sporing · snittordre 885,50 kr = ~88 kr per salg.
//
// TRE TING SOM STYRER HVOR LENKEN KAN STÅ:
//
// 1. `epi` er inntektsmåling per artikkel. Verdien følger med transaksjonen i
//    Adtractions API, så vi kan se hvilken guide som faktisk tjener. Den kan
//    IKKE ettermonteres — sporingen finnes bare for klikk som bar taggen. Sett
//    den alltid til artikkel-slugen.
//
// 2. Rekkefølgen i URL-en er ikke valgfri: `epi` før `url`, og `url` sist.
//    Mål-URL-en prosentkodes. Avlest fra Adtractions egen lenkegenerator.
//
// 3. Vi lenker til KATEGORI, ikke enkeltprodukt. Philips-lenka døde da den ene
//    modellen ble utsolgt og produktsiden 404-et; en kategoriside gjør ikke det.
//    Det holder oss også innenfor programregel 4, som krever at pris- og
//    lagerinformasjon vi oppgir er korrekt — oppgir vi ingen, blir ingenting
//    utdatert.
export function kost1Deeplink(dest: string, epi: string): string {
  return 'https://go.adt284.net/t/t?a=2085431311&as=2092991948&t=2&tk=1'
    + `&epi=${encodeURIComponent(epi)}&url=${encodeURIComponent(dest)}`;
}

/** Proteinpulver-kategorien: whey, isolat, clear whey, laktosefri, vegansk,
 *  casein. Verifisert på lager 31.8.2026. */
export const KOST1_PROTEINPULVER = kost1Deeplink(
  'https://www.kost1.no/categories/proteinpulver',
  'proteinpulver-guide',
);

/** Kreatin i pulverform — den formen forskningen faktisk er gjort på, og den
 *  billigste per gram. Kost1 har ingen «kreatin»-forelderkategori (den i menyen
 *  er bare en meny); /categories/kreatin gir 404. Underkategoriene er
 *  kreatin-pulver, -kapsler, -med-smak, -gummies og -styrke. Verifisert 200
 *  31.8.2026. */
export const KOST1_KREATIN = kost1Deeplink(
  'https://www.kost1.no/categories/kreatin-pulver',
  'kreatin-guide',
);

/** IKKE I BRUK ENNÅ — fiberhusk. Destinasjonen står klar, men lenken skal ikke
 *  ut før BEGGE blokkeringene under er borte. Sjekk med
 *  `npx tsx scripts/kost1-lagersjekk.ts`.
 *
 *  1. FORM: Kost1 fører psyllium kun som kapsler. Guiden vår handler om baking
 *     («hvor mye fiberhusk i brød»), og du baker ikke med kapsler.
 *  2. LAGER: begge psyllium-varene sto «Utsolgt» 31.8.2026 — både
 *     /products/psyllium-husk-180-kapsler og 3-pakningen. Å lenke dit nå ville
 *     vært Philips-feilen om igjen.
 *
 *  Blir pulverform tilgjengelig, er lenken én linje: bytt destinasjon og legg
 *  den inn ved avsnittet om fibertilskudd — ikke ved bakedelen. */
export const KOST1_FIBERHUSK_KANDIDAT = kost1Deeplink(
  'https://www.kost1.no/categories/fordoyelse',
  'fiberhusk-guide',
);

/** URL-ene lagersjekken overvåker for fiberhusk. */
export const KOST1_FIBERHUSK_LAGERSJEKK = [
  'https://www.kost1.no/products/psyllium-husk-180-kapsler',
  'https://www.kost1.no/products/3-pack-psyllium-husk-180-kapsler',
];

// HISTORIKK — fiberhusk-guide. Kost1 fører psyllium utelukkende som
// KAPSLER (DNS Psyllium Husk 180 kapsler / 3 x 180), verifisert i kategoriene
// «Forstoppelse» og «Fordøyelse» 31.8.2026. Guiden vår handler om baking:
// den rangerer på «hvor mye fiberhusk i brød» og «hvordan bruke fiberhusk i
// baking», og du baker ikke med kapsler. En lenke der ville vært et
// intensjonsbrudd og i strid med programregel 4 om villedende innhold.
// Legges inn den dagen Kost1 fører pulverform.
